import { createFeatureSelector, createSelector } from '@ngrx/store';
import dayjs from 'dayjs';

import { Live } from '../../models/home';
import * as fromHome from './home.reducer';

export const selectHomeState = createFeatureSelector<fromHome.State>(
  fromHome.homeFeatureKey,
);

export const selectUIState = createSelector(selectHomeState, (s) => s.ui);
export const selectIsShowingTopbar = createSelector(
  selectUIState,
  (s) => s.isShowingTopbar,
);
export const selectIsShowingButtons = createSelector(
  selectUIState,
  (s) => s.isShowingButtons,
);

export const selectLives = createSelector(selectHomeState, (s) =>
  sortLives(filterLives(s.lives, s.ui)),
);
export const selectExternalLives = createSelector(
  selectHomeState,
  (s) => s.externalLives,
);
export const selectPlayers = createSelector(selectHomeState, (s) => s.players);
export const selectConnectionCount = createSelector(
  selectHomeState,
  (s) => s.connectionCount,
);

export const selectIsShowingUpcoming = createSelector(
  selectUIState,
  (s) => s.filter.upcoming,
);
export const selectIsShowingHolo = createSelector(
  selectUIState,
  (s) => s.filter.holo,
);
export const selectIsShowingOther = createSelector(
  selectUIState,
  (s) => s.filter.other,
);
export const selectIsShowingVr = createSelector(
  selectUIState,
  (s) => s.filter.vr,
);
export const selectIsShowingJp = createSelector(
  selectUIState,
  (s) => s.filter.jp,
);
export const selectIsShowingId = createSelector(
  selectUIState,
  (s) => s.filter.id,
);
export const selectIsShowingKr = createSelector(
  selectUIState,
  (s) => s.filter.kr,
);
export const selectIsShowingEn = createSelector(
  selectUIState,
  (s) => s.filter.en,
);
export const selectIsShowingBilibili = createSelector(
  selectUIState,
  (s) => s.filter.bilibili,
);
export const selectIsShowingPrivate = createSelector(
  selectUIState,
  (s) => s.filter.private,
);
export const selectContentFilter = createSelector(
  selectUIState,
  (s) => s.contentFilter,
);

const filterLives = (lives: Live[], ui: fromHome.UIState) => {
  let filtered = lives;

  if (!ui.filter.upcoming) {
    // live
    filtered = filtered.filter((live) => {
      if (live.type === 'live') {
        return true;
      }
      if (live.publishedAt === null) {
        return true;
      }
      const now = dayjs();
      const d = dayjs(live.publishedAt);
      return d.unix() - now.unix() < 3600; // within 1 hour
    });
  } else {
    // upcoming
    filtered = filtered.filter((live) => {
      if (live.publishedAt === null) {
        return true;
      }
      const now = dayjs();
      const d = dayjs(live.publishedAt);
      return d.unix() - now.unix() < 3600 * 24 * 7; // within 1h * 24h * 7days
    });
  }
  // group
  filtered = filtered
    .filter((live) => {
      return (
        (ui.filter.holo && live.groups.includes('hololive')) ||
        (ui.filter.vr && live.groups.includes('vr')) ||
        (ui.filter.jp && live.groups.includes('nijisanjip')) ||
        (ui.filter.id && live.groups.includes('nijisanjiid')) ||
        (ui.filter.kr && live.groups.includes('nijisanjikr')) ||
        (ui.filter.en && live.groups.includes('nijisanjien')) ||
        (ui.filter.other && live.groups.includes('other')) ||
        (ui.filter.private && live.groups.includes('private'))
      );
    })
    // bilibiliのみ既にフィルタされた結果のフィルタ
    .filter((live) => {
      if (ui.filter.bilibili) {
        return true;
      } else {
        return !live.isBilibili;
      }
    });

  // ContentFilter
  const regApex = /apex|ぺっくす|ぺっくしゅ/i;
  const regARK = /ark/i;
  const regFall = /fall\s*guys|フォール\s*ガイズ/i;
  const regMine = /minecraft|マイクラ|マインクラフト|峰倉/i;
  if (!ui.contentFilter.Apex) {
    filtered = filtered.filter((live) => !regApex.test(live.title));
  }
  if (!ui.contentFilter.ARK) {
    filtered = filtered.filter((live) => !regARK.test(live.title));
  }
  if (!ui.contentFilter.Fall) {
    filtered = filtered.filter((live) => !regFall.test(live.title));
  }
  if (!ui.contentFilter.Mine) {
    filtered = filtered.filter((live) => !regMine.test(live.title));
  }
  if (!ui.contentFilter.Etc) {
    filtered = filtered.filter(
      (live) =>
        regApex.test(live.title) ||
        regARK.test(live.title) ||
        regFall.test(live.title) ||
        regMine.test(live.title),
    );
  }
  return filtered;
};

const getTypeOrder = (type: Live['type']) =>
  type === 'live' ? 1 : type === null ? 2 : type === 'upcoming' ? 3 : 9;

const sortLives = (lives: Live[]) =>
  lives.sort((a, b) => {
    // 1. Bilibili is last because bilibili's metric is not viewer count
    if (a.isBilibili !== b.isBilibili) {
      return (a.isBilibili ? 1 : 0) - (b.isBilibili ? 1 : 0);
    }
    // 2. live or not (live is former)
    if (a.type !== b.type) {
      return getTypeOrder(a.type) - getTypeOrder(b.type);
    }
    // 3. viewer desc
    if (a.viewer !== b.viewer) {
      return (b.viewer ?? 0) - (a.viewer ?? 0);
    }
    // 4. publishedAt asc
    if (a.publishedAt?.getTime() !== b.publishedAt?.getTime()) {
      return (
        (a.publishedAt?.getTime() ?? Number.MAX_SAFE_INTEGER) -
        (b.publishedAt?.getTime() ?? Number.MAX_SAFE_INTEGER)
      );
    }
    return 0;
  });
